import React from 'react';

/**
 * Lista de registros: **tabela no desktop, cards no celular** (at-56, frente B).
 *
 * 🔴 **Uma definição de colunas, duas apresentações.** É o componente que encerra
 * uma duplicação real: a at-54 fez o colapso em cards à mão em `/adesoes` (via
 * `data-label` + `::before` no CSS), e a aba de rascunhos renderiza **uma
 * `<table>` E um `<div>` de cards no mesmo arquivo**, com o texto de vazio e de
 * "Carregando..." escrito duas vezes e três funções auxiliares existindo só para
 * não triplicar o JSX. O mesmo padrão está repetido em `/carteira`, no painel
 * `/vistoria` e nas telas de `/ti`.
 *
 * A decisão de Victor que isto atende (at-54, 08/09/2026): *"não gosto de tabelas
 * em mobile"* — a lista **colapsa**, não vira scroller horizontal da página
 * (spec 02 §9).
 *
 * ⚠️ **Componente burro, e deve continuar.** Não busca, não pagina, não ordena,
 * não filtra e não conhece o domínio. Se uma coluna precisar de mais de um caso
 * especial, monte o conteúdo dela no consumidor e passe pronto.
 *
 * ⚠️ **As duas apresentações existem no DOM**; o CSS mostra uma. Para 10 itens por
 * página o custo é irrelevante, e evita o salto de layout de decidir por
 * `matchMedia` no JS. Consequência: **`celula(item)` é chamada duas vezes por
 * item** — mantenha-a pura (nada de contador, `Math.random` ou efeito colateral).
 *
 * ⚠️ O breakpoint do colapso é **fixo em `md` (768px)**, e não uma prop: classes
 * do Tailwind precisam ser estáticas para o JIT gerá-las. 768px é o critério que
 * a at-54 usou na aba de adesões; a de rascunhos usava 720px e passa a seguir
 * este (a diferença de 48px não muda nenhum caso real, e ter dois breakpoints na
 * mesma tela é que era estranho).
 */

export interface ColunaDataList<T> {
  id: string;
  /** Cabeçalho na tabela e rótulo do valor no card. */
  rotulo: string;
  /** ⚠️ Mantenha PURA — é chamada duas vezes por item (ver aviso acima). */
  celula: (item: T) => React.ReactNode;
  /** Vira o título do card. Sem nenhuma marcada, a primeira coluna assume. */
  principal?: boolean;
  /** Vai para a linha secundária do card, junto das outras, separadas por "·". */
  secundaria?: boolean;
  /** Não aparece no card (informação que só cabe no desktop). */
  ocultarNoCard?: boolean;
  alinhamento?: 'esquerda' | 'centro' | 'direita';
}

const ALINHAMENTO: Record<'esquerda' | 'centro' | 'direita', string> = {
  esquerda: 'tw-text-left',
  centro: 'tw-text-center',
  direita: 'tw-text-right',
};

interface PropsDataList<T> {
  itens: T[];
  colunas: ColunaDataList<T>[];
  chave: (item: T) => string;
  /** Botões da linha/card. Ganha coluna própria no fim da tabela. */
  acoes?: (item: T) => React.ReactNode;
  rotuloAcoes?: string;
  /** Descreve a tabela para o leitor de tela. */
  rotuloAcessivel: string;
}

const DataList = <T,>({
  itens,
  colunas,
  chave,
  acoes,
  rotuloAcoes = 'Ações',
  rotuloAcessivel,
}: PropsDataList<T>) => {
  const principal = colunas.find((coluna) => coluna.principal) ?? colunas[0];
  const secundarias = colunas.filter((coluna) => coluna.secundaria && coluna !== principal);
  const restantes = colunas.filter(
    (coluna) => coluna !== principal && !coluna.secundaria && !coluna.ocultarNoCard,
  );

  return (
    <>
      {/* ───────── TABELA (≥768px) ───────── */}
      <div className="tw-hidden tw-overflow-hidden tw-rounded-xl tw-border tw-border-superficie-borda tw-bg-white md:tw-block">
        <table className="tw-w-full tw-border-collapse tw-text-sm" aria-label={rotuloAcessivel}>
          <thead>
            <tr className="tw-border-b tw-border-superficie-borda tw-bg-superficie">
              {colunas.map((coluna) => (
                <th
                  key={coluna.id}
                  scope="col"
                  className={[
                    'tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-texto-suave',
                    ALINHAMENTO[coluna.alinhamento ?? 'esquerda'],
                  ].join(' ')}
                >
                  {coluna.rotulo}
                </th>
              ))}
              {acoes && (
                /* ⚠️ Alinhada à ESQUERDA, como todas as outras colunas. Estava à
                   direita, e aí o rótulo ficava no extremo da tabela, longe dos
                   botões que ele nomeia (Victor, 09/09/2026). */
                <th
                  scope="col"
                  className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-texto-suave"
                >
                  {rotuloAcoes}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {itens.map((item) => (
              <tr
                key={chave(item)}
                className="tw-border-b tw-border-superficie-borda last:tw-border-0 hover:tw-bg-superficie motion-reduce:tw-transition-none"
              >
                {colunas.map((coluna) => (
                  <td
                    key={coluna.id}
                    className={[
                      'tw-px-4 tw-py-3.5 tw-align-middle tw-text-texto-corpo',
                      ALINHAMENTO[coluna.alinhamento ?? 'esquerda'],
                    ].join(' ')}
                  >
                    {coluna.celula(item)}
                  </td>
                ))}
                {acoes && (
                  /* ⚠️ Flex, e não `text-align`: alinhamento por texto depende de
                     o conteúdo ser inline e some assim que uma ação vira bloco (um
                     botão de largura total, por exemplo). `justify-start` para os
                     botões nascerem sob o cabeçalho "Ações". */
                  <td className="tw-px-4 tw-py-3.5">
                    <div className="tw-flex tw-justify-start tw-gap-2">{acoes(item)}</div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ───────── CARDS (<768px) ───────── */}
      <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-3 tw-p-0 md:tw-hidden">
        {itens.map((item) => (
          <li
            key={chave(item)}
            className="tw-rounded-xl tw-border tw-border-superficie-borda tw-bg-white tw-p-4"
          >
            <div className="tw-font-marca tw-text-[15px] tw-font-bold tw-text-texto-titulo [overflow-wrap:anywhere]">
              {principal?.celula(item)}
            </div>

            {secundarias.length > 0 && (
              <div className="tw-mt-0.5 tw-flex tw-flex-wrap tw-items-center tw-gap-x-1.5 tw-text-xs tw-text-texto-suave">
                {secundarias.map((coluna, indice) => (
                  <React.Fragment key={coluna.id}>
                    {indice > 0 && <span aria-hidden="true">·</span>}
                    <span>{coluna.celula(item)}</span>
                  </React.Fragment>
                ))}
              </div>
            )}

            {restantes.length > 0 && (
              <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-1.5">
                {restantes.map((coluna) => (
                  <div key={coluna.id} className="tw-flex tw-items-center tw-gap-2">
                    {/* Sem o cabeçalho da tabela, um valor solto não se explica —
                        principalmente um chip de estado. O rótulo é obrigatório
                        aqui (spec 02 §9: legível sem depender só de cor). */}
                    <span className="tw-shrink-0 tw-text-[11px] tw-uppercase tw-tracking-wide tw-text-texto-desabilitado">
                      {coluna.rotulo}
                    </span>
                    <span className="tw-min-w-0 tw-text-xs tw-text-texto-corpo">
                      {coluna.celula(item)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {acoes && <div className="tw-mt-3">{acoes(item)}</div>}
          </li>
        ))}
      </ul>
    </>
  );
};

export default DataList;
